import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Button, Select, TextInput } from '../../ui';
import type { SeriesAction, SeriesStatus } from '../series/seriesStatus';

export default function StandInForm({ botId }: { botId: string }) {
  const query = useQuery({
    queryKey: ['lobby-lineup', botId],
    queryFn: () =>
      api.get<SeriesStatus>(
        `/api/v1/pool/bot/${encodeURIComponent(botId)}/lobby/lineup`,
      ),
    retry: false,
  });
  const [team, setTeam] = useState<'radiant' | 'dire'>('radiant');
  const [outgoing, setOutgoing] = useState('');
  const [incoming, setIncoming] = useState('');
  const [reason, setReason] = useState('');
  const [request, setRequest] = useState<SeriesAction | null>(null);
  const mutation = useMutation({
    mutationFn: (payload: SeriesAction) =>
      api.post<{ failed: string[] }>(
        `/api/v1/pool/bot/${encodeURIComponent(botId)}/lobby/lineup/actions`,
        payload,
      ),
    onSuccess: () => {
      void query.refetch();
    },
  });
  return (
    <div className="space-y-3 border-t border-line pt-4">
      <p className="text-sm">
        Approve a replacement for this lobby before inviting them. The saved
        team roster stays unchanged.
      </p>
      {query.isError && (
        <p role="alert" className="text-sm text-warn">
          {query.error.message}
        </p>
      )}
      {query.data && (
        <>
          <label className="block text-sm">
            Team
            <Select
              disabled={!!request}
              value={team}
              onChange={(e) => {
                setTeam(e.target.value as 'radiant' | 'dire');
                setOutgoing('');
              }}
            >
              <option value="radiant">Team A / Radiant</option>
              <option value="dire">Team B / Dire</option>
            </Select>
          </label>
          <label className="block text-sm">
            Outgoing player
            <Select
              disabled={!!request}
              value={outgoing}
              onChange={(e) => setOutgoing(e.target.value)}
            >
              <option value="">Select player</option>
              {query.data.teams[team]?.map((id) => (
                <option key={id}>{id}</option>
              ))}
            </Select>
          </label>
          <label className="block text-sm">
            Replacement Steam ID
            <TextInput
              disabled={!!request}
              value={incoming}
              onChange={(e) => setIncoming(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Reason
            <TextInput
              disabled={!!request}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Button
            disabled={
              !outgoing ||
              !incoming.trim() ||
              !reason.trim() ||
              mutation.isSuccess
            }
            loading={mutation.isPending}
            onClick={() => {
              const payload = request ?? {
                id: crypto.randomUUID(),
                expected_revision: query.data!.revision,
                type: 'replace_player',
                reason: reason.trim(),
                team,
                outgoing,
                incoming: incoming.trim(),
                scope: 'current_game',
              };
              setRequest(payload);
              mutation.mutate(payload);
            }}
          >
            {request
              ? 'Retry approval and invitation'
              : 'Approve and invite stand-in'}
          </Button>
          {mutation.isSuccess && (
            <p role="status" className="text-sm text-ok">
              Replacement approved.
              {mutation.data.failed.length
                ? ' Invitation failed; retry from the invite list above.'
                : ' Invitation sent.'}
            </p>
          )}
          {mutation.isError && (
            <p role="alert" className="text-sm text-warn">
              {mutation.error.message}
            </p>
          )}
        </>
      )}
    </div>
  );
}
